import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import {
  Folder, FileText, Film, Image, Music, ArrowRight, Pencil, Check,
  AlertTriangle, CircleAlert, Info,
} from "lucide-react";
import { OriginalDiff, SuggestedDiff } from "@/components/DiffText";

function iconFor(row) {
  if (row.type === "folder") return Folder;
  const ext = (row.original.split(".").pop() || "").toLowerCase();
  if (["mp4", "mov", "avi", "mkv", "webm"].includes(ext)) return Film;
  if (["png", "jpg", "jpeg", "gif", "webp", "heic"].includes(ext)) return Image;
  if (["mp3", "wav", "flac", "aac", "ogg"].includes(ext)) return Music;
  return FileText;
}

function StatusBadge({ row }) {
  if (row.status === "conflict")
    return (
      <span title="Name collision" data-testid={`status-conflict-${row.id}`}>
        <CircleAlert className="h-4 w-4 text-[#FF3B30]" />
      </span>
    );
  if (row.status === "case")
    return (
      <span title="Case-only difference" data-testid={`status-case-${row.id}`}>
        <AlertTriangle className="h-4 w-4 text-[#FEBC2E]" />
      </span>
    );
  if (row.edited)
    return <span title="Manually edited"><Pencil className="h-3.5 w-3.5 text-[#00E5FF]" /></span>;
  if (row.suggested !== row.original)
    return <span title="Will be renamed" className="h-2 w-2 rounded-full bg-[#34C759] inline-block" />;
  return <span className="h-2 w-2 rounded-full bg-white/15 inline-block" title="Unchanged" />;
}

function Row({ row, index, onToggle, onEdit }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(row.suggested);
  useEffect(() => setVal(row.suggested), [row.suggested]);
  const Icon = iconFor(row);
  const changed = row.suggested !== row.original;

  const commit = () => {
    setEditing(false);
    if (val.trim() && val !== row.suggested) onEdit(row.id, val.trim());
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.2, delay: Math.min(index * 0.012, 0.3) }}
      className={`grid grid-cols-[auto_auto_1.3fr_auto_1.3fr_0.7fr_auto] gap-3 items-center text-sm font-mono-ui border-b border-white/5 py-2.5 px-4 hover:bg-white/5 transition-colors duration-200 group ${
        row.status === "conflict" ? "bg-[#FF3B30]/5" : ""
      }`}
      data-testid={`rename-row-${row.id}`}
    >
      <button
        onClick={() => onToggle(row.id)}
        data-testid={`row-toggle-${row.id}`}
        className={`h-4 w-4 rounded-[4px] border flex items-center justify-center transition-colors duration-200 ${
          row.include ? "bg-[#00E5FF] border-[#00E5FF]" : "border-white/25 hover:border-white/50"
        }`}
        aria-label="toggle row"
      >
        {row.include && <Check className="h-3 w-3 text-black" strokeWidth={3} />}
      </button>

      <Icon
        style={{ marginLeft: `${Math.min(row.depth || 0, 8) * 14}px` }}
        className={`h-4 w-4 ${row.type === "folder" ? "text-[#00E5FF]/80" : "text-neutral-500"}`}
      />

      <div className="truncate" title={row.path || row.original}>
        <OriginalDiff original={row.original} suggested={row.suggested} />
      </div>

      <ArrowRight className={`h-3.5 w-3.5 ${changed ? "text-[#00E5FF]" : "text-neutral-700"}`} />

      <div className="flex items-center gap-2 min-w-0">
        {editing ? (
          <input
            autoFocus
            value={val}
            onChange={(e) => setVal(e.target.value)}
            onBlur={commit}
            onKeyDown={(e) => e.key === "Enter" && commit()}
            data-testid={`row-edit-input-${row.id}`}
            className="flex-1 bg-black/60 border border-[#00E5FF]/50 rounded px-2 py-1 text-white outline-none focus:ring-1 focus:ring-[#00E5FF] min-w-0"
          />
        ) : (
          <span className="truncate min-w-0" title={row.suggested}>
            <SuggestedDiff original={row.original} suggested={row.suggested} />
          </span>
        )}
        {row.reason && !editing && (
          <span title={row.reason} data-testid={`row-why-${row.id}`}>
            <Info className="h-3.5 w-3.5 text-neutral-600 hover:text-[#00E5FF] transition-colors duration-200" />
          </span>
        )}
        <button
          onClick={() => (editing ? commit() : setEditing(true))}
          data-testid={`row-edit-btn-${row.id}`}
          className="opacity-40 group-hover:opacity-100 text-neutral-500 hover:text-[#00E5FF] transition-all duration-200 shrink-0"
          aria-label="edit name"
        >
          <Pencil className="h-3.5 w-3.5" />
        </button>
      </div>

      <div className="truncate text-xs text-neutral-600" title={row.parent}>
        {row.parent ? row.parent.split("/").pop() : "—"}
      </div>

      <div className="flex justify-center w-6">
        <StatusBadge row={row} />
      </div>
    </motion.div>
  );
}

export default function RenameTable({ rows, allRowCount, onToggle, onEdit, onToggleAll }) {
  const allSelected = rows.length > 0 && rows.every((r) => r.include);

  return (
    <div className="flex flex-col min-h-0 flex-1">
      <div className="grid grid-cols-[auto_auto_1.3fr_auto_1.3fr_0.7fr_auto] gap-3 items-center px-4 py-2.5 border-b border-white/10 bg-[#0A0A0A] sticky top-0 z-10 text-[10px] uppercase tracking-widest text-neutral-500">
        <button
          onClick={onToggleAll}
          data-testid="toggle-all-btn"
          className={`h-4 w-4 rounded-[4px] border flex items-center justify-center transition-colors duration-200 ${
            allSelected ? "bg-[#00E5FF] border-[#00E5FF]" : "border-white/25 hover:border-white/50"
          }`}
          aria-label="toggle all"
        >
          {allSelected && <Check className="h-3 w-3 text-black" strokeWidth={3} />}
        </button>
        <span></span>
        <span>Original</span>
        <span></span>
        <span>New name</span>
        <span>Folder</span>
        <span>St.</span>
      </div>

      <div className="overflow-y-auto flex-1" data-testid="rename-table-body">
        {rows.length === 0 ? (
          <div className="p-8 text-center text-neutral-500 font-mono-ui text-sm">
            No items match the current filter/search.
          </div>
        ) : (
          rows.map((row, i) => (
            <Row key={row.id} row={row} index={i} onToggle={onToggle} onEdit={onEdit} />
          ))
        )}
      </div>
    </div>
  );
}
